import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import FormFieldWrapper from '@plone/volto/components/manage/Widgets/FormFieldWrapper';

import clearSVG from '@plone/volto/icons/clear.svg';

const messages = defineMessages({
  clearDateTime: {
    id: 'Clear date/time',
    defaultMessage: 'Clear date and time',
  },
});

// Converts an ISO datetime string to the "YYYY-MM-DDTHH:mm" format that
// <input type="datetime-local"> expects, in local time.
const toInputValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const DateTimeNowWidgetComponent = (props) => {
  const { id, value, onChange, resettable, isDisabled, required, formData } =
    props;

  const intl = useIntl();

  const renderWidget = !(id === 'end' && formData?.open_end);

  // If no value is set yet, default it to the current date and time.
  // Only runs once on mount so that explicitly clearing the field later
  // (via the reset button) isn't immediately overridden back to "now".
  useEffect(() => {
    if (!value) {
      onChange(id, new Date().toISOString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onInputChange = (event) => {
    const raw = event.target.value; // "YYYY-MM-DDTHH:mm" local time, or ''
    if (!raw) {
      onChange(id, null);
      return;
    }
    const parsed = new Date(raw);
    onChange(id, Number.isNaN(parsed.getTime()) ? null : parsed.toISOString());
  };

  const onReset = () => onChange(id, null);

  return (
    <FormFieldWrapper {...props}>
      {renderWidget && (
        <div className="date-time-widget-wrapper datetime-now-widget-wrapper">
          <input
            type="datetime-local"
            id={`field-${id}`}
            className="ui input"
            disabled={isDisabled}
            required={required}
            value={toInputValue(value)}
            onChange={onInputChange}
          />
          {resettable && value && (
            <button
              type="button"
              disabled={isDisabled}
              onClick={onReset}
              className="item ui noborder button"
              aria-label={intl.formatMessage(messages.clearDateTime)}
            >
              <Icon name={clearSVG} size="24px" className="close" />
            </button>
          )}
        </div>
      )}
    </FormFieldWrapper>
  );
};

DateTimeNowWidgetComponent.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  wrapped: PropTypes.bool,
  resettable: PropTypes.bool,
};

DateTimeNowWidgetComponent.defaultProps = {
  description: null,
  required: false,
  error: [],
  value: null,
  resettable: true,
};

export default DateTimeNowWidgetComponent;
